import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  addLeaderProposal,
  approveActionPlan,
  commentOnPlan,
  completeActionPlanStep,
  decideArea,
  registerPlanReview,
  removePlanReview,
  requestPlanReview,
  withdrawPlanReview,
  remindAboutPlanReview,
  saveActionPlan,
  setMeasureStatus,
  startEvaluation,
  archiveEvaluatedSurvey,
  archiveInsufficientSurvey,
  askForSignature,
  decideResidualRisk,
  registerSignatureDeviation,
  withdrawSignatureRequest,
  addWorkEnvironmentDataSource,
  closeHighlights,
  closeProposals,
  hideProposal,
  openWorkEnvironmentProposals,
  removeWorkEnvironmentDataSource,
  remindSafetyRepsOfRisk,
  extendWorkEnvironmentDeadline,
  saveWorkEnvironmentInsufficientAssessment,
  saveWorkEnvironmentRiskAssessment,
} from '../../api/workEnvironmentQueries';
import type {
  DataSourceRequest,
  InsufficientAssessmentRequest,
  AreaDecisionType,
  MeasureStatus,
  PlanRequest,
  ProposalRequest,
  RiskAssessmentRequest,
  ResidualRiskRequest,
} from '../../api/workEnvironmentTypes';
import { WorkEnvironmentSurveyByIdQueryKey } from '../queries/useGetWorkEnvironmentSurveyByIdQuery';
import { WorkEnvironmentRiskQueryKey } from '../queries/useWorkEnvironmentRiskQuery';
import { WorkEnvironmentSurveyProgressQueryKey } from '../queries/useWorkEnvironmentSurveyProgress';
import { WorkEnvironmentMeasuresQueryKey } from '../queries/useWorkEnvironmentMeasuresQuery';
import { ActionPlanningQueryKey } from '../queries/useActionPlanningQuery';
import { EvaluationQueryKey } from '../queries/useEvaluationQuery';
import { ArchiveQueryKey, DocumentationQueryKey } from '../queries/useArchiveQuery';
import { invitationLaterText } from '../../pages/WorkEnvironmentSurveyDetail/reminderView';

/** Each step can move the survey on, so the survey, its risk and its progress are fetched again */
const useSurveyMutation = <T, R = void>(surveyId: number, run: (value: T) => Promise<R>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () =>
      Promise.all(
        [
          WorkEnvironmentSurveyByIdQueryKey,
          WorkEnvironmentRiskQueryKey,
          WorkEnvironmentSurveyProgressQueryKey,
          WorkEnvironmentMeasuresQueryKey,
          ActionPlanningQueryKey,
          EvaluationQueryKey,
          ArchiveQueryKey,
          DocumentationQueryKey,
        ].map((key) => queryClient.invalidateQueries({ queryKey: [key, surveyId] })),
      ),
  });
};

export const useSaveRiskAssessmentMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, (data: RiskAssessmentRequest) => saveWorkEnvironmentRiskAssessment(surveyId, data));

export const useRemindSafetyRepsMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, () => remindSafetyRepsOfRisk(surveyId));

export const useExtendDeadlineMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, (deadline: string) => extendWorkEnvironmentDeadline(surveyId, deadline));

export const useSaveInsufficientAssessmentMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, (data: InsufficientAssessmentRequest) =>
    saveWorkEnvironmentInsufficientAssessment(surveyId, data),
  );

export const useAddDataSourceMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, (data: DataSourceRequest) => addWorkEnvironmentDataSource(surveyId, data));

export const useRemoveDataSourceMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, (id: number) => removeWorkEnvironmentDataSource(surveyId, id));

export const useArchiveInsufficientSurveyMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, () => archiveInsufficientSurvey(surveyId));

export const useOpenProposalsMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, () => openWorkEnvironmentProposals(surveyId));

export const useAddLeaderProposalMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, (data: ProposalRequest) => addLeaderProposal(surveyId, data));

export const useHideProposalMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, ({ id, reason }: { id: number; reason: string }) => hideProposal(surveyId, id, reason));

export const useCloseProposalsMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, () => closeProposals(surveyId));

export const useCloseHighlightsMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, () => closeHighlights(surveyId));

export const useSaveActionPlanMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, ({ planId, plan }: { planId: number | null; plan: PlanRequest }) =>
    saveActionPlan(surveyId, planId, plan),
  );

export const useApproveActionPlanMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, ({ planId, deviation }: { planId: number; deviation: string | null }) =>
    approveActionPlan(surveyId, planId, deviation),
  );

export const useRequestPlanReviewMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, (planId: number) => requestPlanReview(surveyId, planId));

export const useWithdrawPlanReviewMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, (planId: number) => withdrawPlanReview(surveyId, planId));

export const useRemindAboutPlanReviewMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, (planId: number) => remindAboutPlanReview(surveyId, planId));

export const useCommentOnPlanMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, ({ planId, text }: { planId: number; text: string }) =>
    commentOnPlan(surveyId, planId, text),
  );

export const useDecideAreaMutation = (surveyId: number) =>
  useSurveyMutation(
    surveyId,
    ({ factorKey, decision, reason }: { factorKey: string; decision: AreaDecisionType; reason: string }) =>
      decideArea(surveyId, factorKey, { decision, reason }),
  );

export const useCompletePlanStepMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, () => completeActionPlanStep(surveyId));

export const useSetMeasureStatusMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, ({ measureId, status }: { measureId: number; status: MeasureStatus }) =>
    setMeasureStatus(surveyId, measureId, status),
  );

export const useRegisterPlanReviewMutation = (surveyId: number) =>
  useSurveyMutation(
    surveyId,
    ({ factorKey, date, note }: { factorKey: string; date: string; note: string }) =>
      registerPlanReview(surveyId, factorKey, { date, note }),
  );

export const useRemovePlanReviewMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, ({ factorKey, on }: { factorKey: string; on: string }) =>
    removePlanReview(surveyId, factorKey, on),
  );

/** Outside the working hours of the team the invitation waits for them, so the user is told when the team gets it */
export const useStartEvaluationMutation = (surveyId: number) => {
  const { t, i18n } = useTranslation();
  return useSurveyMutation(surveyId, async (deadlineInDays: number) => {
    const started = await startEvaluation(surveyId, deadlineInDays);
    const later = invitationLaterText(started?.invitationSendsAt, t, i18n.language);
    if (later) toast.info(later, { autoClose: 8000 });
    return started;
  });
};

export const useDecideResidualRiskMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, ({ factorKey, decision }: { factorKey: string; decision: ResidualRiskRequest }) =>
    decideResidualRisk(surveyId, factorKey, decision),
  );

export const useArchiveEvaluatedSurveyMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, () => archiveEvaluatedSurvey(surveyId));

export const useAskForSignatureMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, () => askForSignature(surveyId));

export const useWithdrawSignatureMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, () => withdrawSignatureRequest(surveyId));

export const useRegisterSignatureDeviationMutation = (surveyId: number) =>
  useSurveyMutation(surveyId, (reason: string) => registerSignatureDeviation(surveyId, reason));

/** The evaluation is a survey of its own, and the page of the first survey shows it */
export const useExtendEvaluationDeadlineMutation = (baselineId: number, evaluationId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deadline: string) => extendWorkEnvironmentDeadline(evaluationId, deadline),
    onSuccess: () =>
      Promise.all(
        [EvaluationQueryKey, WorkEnvironmentSurveyProgressQueryKey].map((key) =>
          queryClient.invalidateQueries({ queryKey: [key, key === EvaluationQueryKey ? baselineId : evaluationId] }),
        ),
      ),
  });
};
